/**
 * readGeneratedMotionExtras — read the generation provenance an exported clip
 * carries in its glTF scene extras (scene.userData.aosGeneratedMotion, written by
 * bodyMotionExport). Loud on failure — a fetch/parse error THROWS (CLAUDE.md: no
 * silent fallbacks on server data); a well-formed non-generated GLB returns null.
 */
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { fetchGlbBytesCached } from './glbCache.js'

// One shared Draco decoder for every provenance read — matches AvatarModel's
// runtime capability (a custom clip may be Draco-compressed) without allocating
// a fresh loader per inspector click.
const _dracoLoader = new DRACOLoader().setDecoderPath('/draco/')

/**
 * Pure extractor: pull the provenance marker off a parsed glTF, or null.
 * @param {{scene?: {userData?: object}}} gltf a GLTFLoader parse result
 * @returns {object|null} the aosGeneratedMotion extras, or null when absent
 */
export function extractGeneratedMotionExtras(gltf) {
  return gltf?.scene?.userData?.aosGeneratedMotion ?? null
}

/**
 * Parse GLB bytes and return the generated-motion extras (or null).
 * @param {ArrayBuffer} buf binary GLB bytes
 * @returns {Promise<object|null>}
 */
export function parseGeneratedMotionExtras(buf) {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader().setDRACOLoader(_dracoLoader)
    loader.parse(
      buf,
      '',
      (gltf) => resolve(extractGeneratedMotionExtras(gltf)),
      (err) => reject(err instanceof Error ? err : new Error('GLTFLoader parse failed: ' + err)),
    )
  })
}

/**
 * Fetch an animation asset's GLB by its file_url and read the generation
 * provenance. Throws on any fetch/parse failure (loud); returns null only for a
 * well-formed GLB with no generated-motion marker.
 *
 * Fetches through fetchGlbBytesCached (the same animCache bucket + loud
 * empty/HTML guards AvatarModel's boot loader uses) so selecting a clip whose
 * bytes are already on disk doesn't re-download a multi-MB GLB just to read its
 * scene extras.
 * @param {string} url the asset's file_url
 * @param {string|number|null} [version] cache version token (the asset's
 *   updatedAt / id) — must match the boot loader's key to hit the warm entry.
 * @returns {Promise<object|null>}
 */
export async function readGeneratedMotionExtras(url, version = null) {
  const buf = await fetchGlbBytesCached(url, version)
  return parseGeneratedMotionExtras(buf)
}
